import { useState } from 'react';
import { describeMatchPoolTier } from '@/lib/match-pool-tier';
import { describeMatchBlock } from '@/lib/match-eligibility';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useCustom, useApiUrl } from '@refinedev/core';
import { formatDate, formatBirthdate, formatDateOrNever, calculateAge, cn, resolveUserDisplayName } from '@/lib/utils';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { UserNotesCard } from './UserNotesCard';
import { SafetyTimelineCard } from './SafetyTimelineCard';
import {
  ArrowLeft,
  User,
  Image,
  Heart,
  MessageSquare,
  Shield,
  UserX,
  UserCheck,
  Trash2,
  Mail,
  Phone,
  Calendar,
  MapPin,
  AlertTriangle,
  Activity,
  LogIn,
  Settings,
  MessageCircle,
  Filter,
  Gift,
  Sparkles,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  Wrench,
  Send,
  CreditCard,
  FlaskConical,
} from 'lucide-react';
import { GrantPromotionModal } from '@/components/coupons/GrantPromotionModal';
import { SetTierModal } from '@/components/subscriptions/SetTierModal';
import { adminFetch, AdminApiError } from '@/providers/admin-api';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { toast } from '@/hooks/useToast';
import {
  enableTester,
  disableTester,
  validateTesterReason,
  describeTesterToggleError,
  TESTER_REASON_MAX_LENGTH,
  TESTER_TOGGLE_LEAD,
  TESTER_TOGGLE_EFFECTS,
  TESTER_TOGGLE_AUDIT_NOTE,
  type TesterToggleDirection,
} from './tester-toggle';

interface UserDetails {
  id: string;
  email: string;
  phone: string | null;
  display_name: string;
  first_name: string | null;
  last_name: string | null;
  birthdate: string | null;
  gender: string | null;
  pronouns: string | null;
  bio: string | null;
  short_bio: string | null;
  location_text: string | null;
  location_city: string | null;
  location_state: string | null;
  location_country: string | null;
  geo_lat: number | null;
  geo_lon: number | null;
  occupation: string | null;
  relationship_goals: string | null;
  relationship_status: string | null;
  relationship_structure: string | null;
  sexual_orientation: string | null;
  search_radius_km: number | null;
  willing_to_relocate: boolean | null;
  interests: string[] | null;
  status: 'active' | 'suspended' | 'deleted';
  created_at: string;
  updated_at: string | null;
  last_active_at: string | null;
  last_session_at: string | null;
  // Setup wizard / onboarding completion flags
  signup_wizard_complete: boolean;
  hard_filters_complete: boolean;
  profile_complete: boolean;
  onboarding_complete: boolean;
  onboarding_step: number | null;
  // The REAL matching-eligibility (canonical Round-1 gate), surfaced by
  // getUserDetails. Source-agnostic (self-answers count), unlike the
  // Abby-voice-only `onboarding_complete` flag above. Optional for backward
  // compat with any cached/partial response.
  /** Match-pool permanence tier (migration 390). Absent on an API older than the view. */
  match_pool_tier?: 'active' | 'restricted' | 'dormant' | null;
  /** Shown matches in a row, window closed, with no app open since. */
  match_pool_unopened_streak?: number | null;
  matching_eligibility?: {
    eligible: boolean;
    reason?: string;
    isFake?: boolean;
    isTester?: boolean;
  };
  // Operational
  is_tester: boolean;
  is_fake: boolean;
  locale: string | null;
  timezone: string | null;
  coaching_active: boolean;
  subscription_tier: string;
  photos_count: number;
  matches_count: number;
  // `messages_count` removed 2026-05-11 — in-app messaging excised
  // in PR #619. Backend admin.ts still SELECTs against core.messages
  // but the count decays to 0 as the table drains; the UI was actively
  // misleading for internal compliance reviews. Backend SELECT will be
  // removed in the follow-up DB drop migration PR.
  reports_filed: number;
  reports_received: number;
  // Verification & trust (user-360 — null when no verification_trust row).
  blue_check?: boolean | null;
  trust_score?: number | null;
  id_verification_status?: string | null;
  government_id_verified?: boolean | null;
  face_match_verified?: boolean | null;
  liveness_verified?: boolean | null;
  email_verified?: boolean | null;
  phone_verified?: boolean | null;
  verification_overall_status?: string | null;
  suspended_at: string | null;
  suspended_by: string | null;
  suspension_reason: string | null;
  suspension_expires_at: string | null;
  deleted_at: string | null;
  deleted_by: string | null;
  deletion_reason: string | null;
  // Admin-invite provenance (Topic 4 PR-1). Populated when an admin
  // created the user via POST /v1/admin/app-users; NULL for organic
  // self-signups. JOINed from core.admin_users so the inviter's email +
  // display_name surface inline without a second request.
  created_by_admin_id: string | null;
  invite_email_sent_at: string | null;
  created_by_admin_email: string | null;
  created_by_admin_name: string | null;
  // Private profile block (slim — 2026-05-26 audit). Only height_cm +
  // precise lat/lon are actually populated in core.profiles_private; the
  // other 11 columns we used to project here are 0/8519 rows. NULL when
  // no row exists for the user.
  private_profile: PrivateProfileSlim | null;
  // Canonical identity / lifestyle "about me" data, sourced from
  // hard_filters.responses_me (the same data the Matching Preferences tab
  // reads). Present on every user who completed the hard-filters wizard.
  identity_hard_filters: IdentityHardFilter[];
}

interface PrivateProfileSlim {
  height_cm: number | null;
  precise_lat: number | null;
  precise_lon: number | null;
}

interface IdentityHardFilter {
  question_key: string;
  category: string | null;
  question_text: string | null;
  question_type: string | null;
  selected_options: string[] | null;
  numeric_value: number | null;
  numeric_unit: string | null;
  updated_at: string;
}

interface OnboardingAnswer {
  question_id: string;
  question_code: string;
  question_text: string;
  category: string | null;
  screen_group: string | null;
  question_type: string;
  display_order: number;
  text_value: string | null;
  numeric_value: number | null;
  raw_response: string | null;
  parse_confidence: number | null;
  parsed_by: string | null;
  answered_at: string;
  conversation_id: string | null;
  option_texts: string[];
  option_codes: string[];
}

interface OnboardingPreferenceAnswer {
  question_id: string;
  question_code: string;
  question_text: string;
  category: string | null;
  screen_group: string | null;
  question_type: string;
  display_order: number;
  min_value: number | null;
  max_value: number | null;
  importance: string | null;
  is_dealbreaker: boolean;
  answered_at: string;
  acceptable_option_texts: string[];
  acceptable_option_codes: string[];
}

interface OnboardingSummary {
  id: string;
  section_id: string;
  section_slug: string | null;
  section_title: string | null;
  section_order: number | null;
  round: number;
  summary: string;
  summary_es: string | null;
  source_question_ids: string[] | null;
  source_answer_count: number | null;
  llm_model: string | null;
  llm_latency_ms: number | null;
  generated_at: string;
  updated_at: string | null;
}

interface MatchPreferences {
  user_id: string;
  age_min: number | null;
  age_max: number | null;
  distance_km: number | null;
  gender_preferences: string[] | null;
  pref_ethnicities: string[] | null;
  pref_languages: string[] | null;
  pref_lifestyles: unknown;
  pref_relationship_structure: string | null;
  pref_age_min: number | null;
  pref_age_max: number | null;
  pref_distance_km: number | null;
  pref_gender_identities: string[] | null;
  created_at: string | null;
  updated_at: string | null;
}

interface OnboardingAnswersResponse {
  user_id: string;
  answers: OnboardingAnswer[];
  preference_answers: OnboardingPreferenceAnswer[];
  summaries: OnboardingSummary[];
  match_preferences: MatchPreferences | null;
  completion: {
    total_active_questions: number;
    total_abby_guided_questions: number;
    answered_count: number;
    preference_answered_count: number;
    abby_guided_answered: number;
    summaries_count: number;
  };
}

interface AbbySession {
  id: string;
  user_id: string;
  session_id: string;
  timestamp: string;
  duration_minutes?: number;
  conversation_summary: string;
  topics_covered: string[];
  key_insights: string[];
  action_items: string[];
  emotional_tone?: string;
  related_match_ids?: string[];
  created_at: string;
}

interface AbbySessionsResponse {
  user_id: string;
  sessions: AbbySession[];
  // 2026-05-26 activity counters from core.coaching_events. Lets the
  // matchmaker distinguish "user never coached" (everything 0) from
  // "user coached extensively but Abby's summary tool didn't fire"
  // (high tool-call counts + zero S3 summaries).
  activity: {
    total_tool_calls: number;
    distinct_session_ids: number;
    last_tool_call_at: string | null;
    session_completed_events: number;
  };
}

interface Photo {
  id: string;
  url: string;
  is_primary: boolean;
  uploaded_at: string;
  /** The section the photo is in (getUserPhotos, 2026-10-13): a restored refused photo can be in either. */
  visibility?: 'public' | 'private';
  moderation_status?: string;
}

interface Match {
  id: string;
  matched_user_id: string;
  matched_user_name: string | null;
  matched_user_email: string | null;
  matched_at: string;
  status: string | null;
  unmatched_at: string | null;
  presented_at: string | null;
  decided_at: string | null;
  activated_at: string | null;
  my_decision: 'interested' | 'pass' | null;
  their_decision: 'interested' | 'pass' | null;
  my_pics_shared: boolean;
  their_pics_shared: boolean;
  my_pics_viewed: boolean;
  their_pics_viewed: boolean;
  // `messages_count` removed 2026-05-11 — see UserDetails interface
  // comment above for rationale.
}

interface UserActivity {
  id: string;
  action: string;
  details: string | null;
  ip_address: string | null;
  created_at: string;
}

interface HardFilterSelfAnswer {
  question_key: string;
  category: string;
  answer_type: string;
  selected_options: string[] | null;
  numeric_value: number | null;
  updated_at: string;
}

interface HardFilterPreferenceAnswer {
  question_key: string;
  category: string;
  answer_type: string;
  selected_options: string[] | null;
  range_min: number | null;
  range_max: number | null;
  is_dealbreaker: boolean;
  importance: string | null;
  updated_at: string;
}

interface HardFiltersCompletion {
  total_questions: number;
  self_answered: number;
  preference_answered: number;
  dealbreaker_count: number;
}

interface HardFiltersResponse {
  user_id: string;
  self_answers: HardFilterSelfAnswer[];
  preference_answers: HardFilterPreferenceAnswer[];
  completion: HardFiltersCompletion;
}

const QUESTION_KEY_LABELS: Record<string, string> = {
  religion: 'Religion',
  height: 'Height',
  children: 'Children',
  wants_children: 'Wants Children',
  smoking: 'Smoking',
  drinking: 'Drinking',
  political_views: 'Political Views',
  education: 'Education',
  body_type: 'Body Type',
};

const getQuestionLabel = (key: string): string =>
  QUESTION_KEY_LABELS[key] || key;

const tabs = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'wizard', label: 'Setup Wizard', icon: Settings },
  { id: 'onboarding', label: 'Onboarding', icon: MessageCircle },
  { id: 'coaching', label: 'Abby Coaching', icon: Sparkles },
  { id: 'hardFilters', label: 'Matching Preferences', icon: Filter },
  { id: 'photos', label: 'Photos', icon: Image },
  { id: 'matches', label: 'Matches', icon: Heart },
  { id: 'activity', label: 'Activity', icon: MessageSquare },
];


export function UserShowPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiUrl = useApiUrl();
  const { hasPermission } = useAdminPermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'profile';
  const setActiveTab = (tab: string) => {
    setSearchParams({ tab }, { replace: true });
  };
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [showTesterModal, setShowTesterModal] = useState(false);
  const [testerReason, setTesterReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [tierModalOpen, setTierModalOpen] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(false);

  // Fetch user details
  const { data: userData, isLoading: userLoading, isError: userError, refetch } = useCustom<UserDetails>({
    url: `/v1/admin/users/${id}`,
    method: 'get',
  });

  // Fetch user's active BETA cohort segments (P1.10 follow-up). Always-on
  // (not tab-gated) — shown as badges in the page header for at-a-glance
  // cohort context. Lightweight (4 rows max per user, partial-unique index
  // on (user_id, segment) WHERE revoked_at IS NULL keeps the query cheap).
  const { data: segmentsData } = useCustom<{
    user_id: string;
    segments: Array<{
      id: string;
      segment: 'friends_family' | 'founding_100' | 'influencer' | 'beta_general';
      source: string;
      granted_at: string;
      granted_by: string | null;
      notes: string | null;
    }>;
  }>({
    url: `/v1/admin/users/${id}/segments`,
    method: 'get',
    queryOptions: { enabled: !!id, staleTime: 60000 },
  });

  // Fetch user photos
  const { data: photosData, isLoading: photosLoading } = useCustom<Photo[]>({
    url: `/v1/admin/users/${id}/photos`,
    method: 'get',
    queryOptions: {
      enabled: activeTab === 'photos',
    },
  });

  // Fetch user matches
  const { data: matchesData, isLoading: matchesLoading } = useCustom<Match[]>({
    url: `/v1/admin/users/${id}/matches`,
    method: 'get',
    queryOptions: {
      enabled: activeTab === 'matches',
    },
  });

  // Fetch hard filters
  const { data: hardFiltersData, isLoading: hardFiltersLoading, isError: hardFiltersError, refetch: refetchHardFilters } = useCustom<HardFiltersResponse>({
    url: `/v1/admin/users/${id}/hard-filters`,
    method: 'get',
    queryOptions: {
      enabled: activeTab === 'hardFilters' && !!id,
      retry: 2,
      staleTime: 30000,
    },
  });

  // Fetch user activity
  const { data: activityData, isLoading: activityLoading, isError: activityError, refetch: refetchActivity } = useCustom<UserActivity[]>({
    url: `/v1/admin/users/${id}/activity`,
    method: 'get',
    queryOptions: {
      enabled: activeTab === 'activity',
      retry: 2,
      staleTime: 30000,
    },
  });

  // Fetch onboarding answers (Abby-guided questions in core.user_answers).
  // Distinct from "hard filters" tab — that one shows hard_filters.responses_*
  // (structured matching criteria). Onboarding answers are the rich coaching
  // answers Abby captures during the voice/text intake flow. As of 2026-05-25
  // the response also includes `summaries` (onboarding.summaries — Abby's
  // per-section narrative summaries) and `match_preferences` (the canonical
  // core.match_preferences row).
  const { data: onboardingData, isLoading: onboardingLoading, isError: onboardingError, refetch: refetchOnboarding } = useCustom<OnboardingAnswersResponse>({
    url: `/v1/admin/users/${id}/onboarding-answers`,
    method: 'get',
    queryOptions: {
      enabled: activeTab === 'onboarding' && !!id,
      retry: 2,
      staleTime: 30000,
    },
  });

  // Fetch Abby coaching session summaries (S3-backed). Tab-gated to avoid
  // an extra round-trip on profile loads where no admin will read them.
  const { data: abbySessionsData, isLoading: abbySessionsLoading, isError: abbySessionsError, refetch: refetchAbbySessions } = useCustom<AbbySessionsResponse>({
    url: `/v1/admin/users/${id}/abby-sessions`,
    method: 'get',
    queryOptions: {
      enabled: activeTab === 'coaching' && !!id,
      retry: 2,
      staleTime: 30000,
    },
  });

  // Coaching-tab row-expansion state (one row open at a time keeps the page
  // visually clean — admins can scan headers then drill into one session).
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);

  // Fetch per-user coupon claims + redemption history
  const { data: couponsData, isLoading: couponsLoading, refetch: refetchCoupons } = useCustom<{
    claims: Array<{
      claim_id: string;
      promotion_id: string;
      promotion_name: string;
      source: string;
      paywall_type: string;
      coupons_remaining: number;
      coupons_granted: number;
      status: 'active' | 'expired' | 'revoked';
      expires_at: string | null;
      granted_at: string;
      granted_by_admin_id: string | null;
      grant_source_event: string;
    }>;
    redemptions: Array<{
      redemption_id: string;
      claim_id: string;
      promotion_name: string;
      paywall_type: string;
      paywall_event_id: string;
      status: 'held' | 'consumed' | 'refunded';
      held_at: string;
      consumed_at: string | null;
      refunded_at: string | null;
      refund_reason: string | null;
    }>;
    total_claims: number;
    total_redemptions: number;
  }>({
    url: `/v1/admin/users/${id}/coupons`,
    method: 'get',
    queryOptions: {
      enabled: activeTab === 'profile' && !!id,
      staleTime: 30000,
    },
  });

  const user = userData?.data;
  const photos = photosData?.data ?? [];
  const matches = matchesData?.data ?? [];
  const activities = activityData?.data ?? [];
  const hardFilters = hardFiltersData?.data;
  const onboarding = onboardingData?.data;

  // API action handlers
  const handleSuspend = async (reason: string, expiresAt?: string) => {
    setActionLoading(true);
    setActionError(null);
    try {
      await adminFetch(`${apiUrl}/v1/admin/users/${id}/suspend`, {
        method: 'POST',
        body: JSON.stringify({
          reason,
          expires_at: expiresAt || null,
        }),
      });
      setShowSuspendModal(false);
      await refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to suspend user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspend = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      await adminFetch(`${apiUrl}/v1/admin/users/${id}/unsuspend`, {
        method: 'POST',
      });
      await refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to unsuspend user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (reason: string) => {
    setActionLoading(true);
    setActionError(null);
    try {
      await adminFetch(`${apiUrl}/v1/admin/users/${id}`, {
        method: 'DELETE',
        body: JSON.stringify({ reason }),
      });
      setShowDeleteModal(false);
      navigate('/users');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  // Flip core.users_core.is_tester (task 7.11). Direction follows the
  // user's CURRENT state — the same account is never both, so there is no
  // separate "which one did they mean" branch beyond that read. Reuses
  // actionLoading/actionError like handleSuspend/handleDelete: only one of
  // Suspend/Delete/Tester modals is ever open at a time.
  const handleTesterToggle = async (reason: string) => {
    if (!user || !id) return;
    const direction: TesterToggleDirection = user.is_tester ? 'disable' : 'enable';
    setActionLoading(true);
    setActionError(null);
    try {
      if (direction === 'enable') {
        await enableTester(apiUrl, id, reason);
      } else {
        await disableTester(apiUrl, id, reason);
      }
      setShowTesterModal(false);
      setTesterReason('');
      await refetch();
    } catch (err) {
      setActionError(describeTesterToggleError(err, direction));
    } finally {
      setActionLoading(false);
    }
  };

  // Resend the Cognito invite to an admin-invited user who hasn't signed
  // in yet. Eligibility (admin-invited + never-signed-in) is enforced by
  // the backend, which returns 409 (RFC7807 problem+json — message in
  // body.title) for organic / already-active users; we surface whatever
  // it returns rather than re-deriving the rule client-side.
  const handleResendInvite = async () => {
    setResendingInvite(true);
    try {
      await adminFetch(`${apiUrl}/v1/admin/app-users/${id}/resend-invite`, {
        method: 'POST',
      });
      toast({
        title: 'Invite resent',
        description: 'A fresh Cognito invite email was sent.',
      });
    } catch (err) {
      let message = 'Failed to resend invite';
      if (err instanceof AdminApiError) {
        const body = err.body as { title?: string; message?: string } | undefined;
        message = body?.title ?? body?.message ?? err.message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      toast({
        title: 'Resend failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setResendingInvite(false);
    }
  };

  // Helper to get activity icon
  const getActivityIcon = (action: string) => {
    switch (action.toLowerCase()) {
      case 'login':
        return LogIn;
      case 'profile_update':
      case 'settings_update':
        return Settings;
      case 'match':
        return Heart;
      case 'message':
        return MessageCircle;
      default:
        return Activity;
    }
  };

  if (userLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (userError || !user) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/users')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to users
        </button>
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
          Failed to load user details. The user may not exist.
        </div>
      </div>
    );
  }

  // calculateAge + formatBirthdate are imported from '@/lib/utils' — calendar-safe
  // (TZ-independent); do NOT reintroduce a local `new Date(birthdate)` version.

  const userName = resolveUserDisplayName(user);

  return (
    <div className="space-y-6">
      {/* Breadcrumb navigation */}
      <Breadcrumb
        items={[
          { label: 'Users', href: '/users' },
          { label: userName },
        ]}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/users')}
            aria-label="Back to users list"
            className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold">
              {userName}
            </h2>
            <p className="text-muted-foreground">{user.email}</p>
          </div>
          <StatusBadge status={user.status} />
          {/* Tester badge (task 7.11 follow-up) — same style/gating as
              pages/users/list.tsx ~849, so the toggle control isn't the
              only indicator of is_tester on this page. */}
          {user.is_tester && !user.is_fake && (
            <span className="rounded bg-sky-500 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-white">
              tester
            </span>
          )}
          <SegmentBadges segments={segmentsData?.data?.segments ?? []} />
          {/* Admin-invited chip (Topic 4 PR-2). Renders only when
              created_by_admin_id is set — organic self-signups don't
              show this. Inviter email + display_name come from the
              JOIN to core.admin_users in getUserDetails. */}
          {user.created_by_admin_id && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 ring-1 ring-blue-200"
              title="This user was invited by an admin via the Invite-app-user flow"
            >
              <Wrench className="h-3 w-3" />
              Admin-invited
              {user.created_by_admin_email && (
                <span className="text-blue-600">
                  · by {user.created_by_admin_name || user.created_by_admin_email}
                </span>
              )}
              {user.invite_email_sent_at && (
                <span className="text-blue-600">
                  · {formatDate(user.invite_email_sent_at)}
                </span>
              )}
            </span>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {/* Resend invite — admin-invited users who haven't signed in yet.
              Signal: created_by_admin_id (admin-invited) + never-active
              (no last session / activity). Backend re-enforces eligibility
              and 409s organic / already-active users. */}
          {hasPermission('users.create') &&
            user.created_by_admin_id &&
            !user.last_session_at &&
            !user.last_active_at &&
            user.status !== 'deleted' && (
              <button
                onClick={handleResendInvite}
                disabled={resendingInvite}
                title="Only works for admin-invited users who haven't signed in yet."
                className="flex items-center gap-2 rounded-lg border border-blue-500 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
              >
                {resendingInvite ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Resend invite
              </button>
            )}
          {user.status === 'active' && (
            <button
              onClick={() => setShowSuspendModal(true)}
              className="flex items-center gap-2 rounded-lg border border-yellow-500 px-4 py-2 text-sm font-medium text-yellow-600 hover:bg-yellow-50"
            >
              <UserX className="h-4 w-4" />
              Suspend
            </button>
          )}
          {user.status === 'suspended' && (
            <button
              onClick={handleUnsuspend}
              disabled={actionLoading}
              className="flex items-center gap-2 rounded-lg border border-green-500 px-4 py-2 text-sm font-medium text-green-600 hover:bg-green-50 disabled:opacity-50"
            >
              {actionLoading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-green-500 border-t-transparent" />
              ) : (
                <UserCheck className="h-4 w-4" />
              )}
              Unsuspend
            </button>
          )}
          {/* Mark as tester / Remove tester (task 7.11). Never shown for
              fake/simulated accounts — is_tester has no meaning there. */}
          {!user.is_fake && hasPermission('users.tester.toggle') && (
            <button
              onClick={() => {
                setTesterReason('');
                setActionError(null);
                setShowTesterModal(true);
              }}
              className="flex items-center gap-2 rounded-lg border border-purple-500 px-4 py-2 text-sm font-medium text-purple-600 hover:bg-purple-50"
            >
              <FlaskConical className="h-4 w-4" />
              {user.is_tester ? 'Remove tester' : 'Mark as tester'}
            </button>
          )}
          {user.status !== 'deleted' && (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-2 rounded-lg border border-destructive px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Suspension banner */}
      {user.status === 'suspended' && user.suspension_reason && (
        <div className="rounded-lg border border-yellow-500 bg-yellow-50 p-4">
          <div className="flex items-center gap-2 font-medium text-yellow-800">
            <Shield className="h-4 w-4" />
            Account Suspended
          </div>
          <p className="mt-1 text-sm text-yellow-700">
            Reason: {user.suspension_reason}
          </p>
          {user.suspension_expires_at && (
            <p className="mt-1 text-sm text-yellow-700">
              Expires: {formatDate(user.suspension_expires_at)}
            </p>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b">
        <nav className="-mb-px flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:border-muted hover:text-foreground'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'profile' && (
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Basic Info */}
            <div className="rounded-lg border bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold">Basic Information</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <div className="text-sm text-muted-foreground">Full Name</div>
                    <div className="font-medium">{resolveUserDisplayName(user)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <div className="text-sm text-muted-foreground">Email</div>
                    <div className="font-medium">{user.email}</div>
                  </div>
                </div>
                {user.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="text-sm text-muted-foreground">Phone</div>
                      <div className="font-medium">{user.phone}</div>
                    </div>
                  </div>
                )}
                {user.birthdate && (
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="text-sm text-muted-foreground">Age</div>
                      <div className="font-medium">{calculateAge(user.birthdate)} years old</div>
                    </div>
                  </div>
                )}
                {user.location_text && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="text-sm text-muted-foreground">Location</div>
                      <div className="font-medium">{user.location_text}</div>
                    </div>
                  </div>
                )}
                {user.gender && (
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="text-sm text-muted-foreground">Gender</div>
                      {/* Humanize the canonical underscore taxonomy (e.g.
                          transgender_woman → "transgender woman") — capitalize
                          styles the first letter. */}
                      <div className="font-medium capitalize">{user.gender?.replace(/_/g, ' ')}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Account Stats */}
            <div className="rounded-lg border bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold">Account Statistics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg bg-muted/50 p-4">
                  <div className="text-2xl font-bold">{user.photos_count}</div>
                  <div className="text-sm text-muted-foreground">Photos</div>
                </div>
                <div className="rounded-lg bg-muted/50 p-4">
                  <div className="text-2xl font-bold">{user.matches_count}</div>
                  <div className="text-sm text-muted-foreground">Matches</div>
                </div>
                {/* Messages stat-card removed 2026-05-11 — see UserDetails
                    interface comment for context. */}
                <div className="rounded-lg bg-muted/50 p-4">
                  <div className="text-2xl font-bold capitalize">{user.subscription_tier}</div>
                  <div className="text-sm text-muted-foreground">Subscription</div>
                </div>
              </div>

              <div className="mt-6 space-y-3 border-t pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Joined</span>
                  <span className="font-medium">{formatDate(user.created_at)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Last Active</span>
                  <span className="font-medium">
                    {formatDateOrNever(user.last_active_at)}
                  </span>
                </div>
                <div className="flex justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">Match-eligible</span>
                  <span className="text-right">
                    <span className={`font-medium ${eligibilityTone(user.matching_eligibility)}`}>
                      {eligibilityWord(user.matching_eligibility)}
                    </span>
                    {/* WHY the reason is here and not only on the Wizard tab:
                        this card is where an operator reads "Not eligible"
                        directly above "Match pool: Active", and a verdict with
                        no cause sends them hunting through tabs. Same clause
                        map as the create screen, so one member reads the same
                        way everywhere. */}
                    {user.matching_eligibility && !user.matching_eligibility.eligible && (
                      <span className="block text-xs text-muted-foreground">
                        {describeMatchBlock(user.matching_eligibility.reason)}
                      </span>
                    )}
                  </span>
                </div>
                {user.match_pool_tier && (
                  <div className="flex justify-between gap-3 text-sm">
                    <span className="text-muted-foreground">Match pool</span>
                    <span
                      className={`text-right font-medium ${
                        user.match_pool_tier === 'active'
                          ? 'text-green-600'
                          : user.match_pool_tier === 'restricted'
                            ? 'text-yellow-600'
                            : 'text-red-600'
                      }`}
                      title="5 shown matches in a row with no app open → Restricted (matched on Mondays only); Restricted and no app open for 90 days → Dormant (not matched). Any app open → Active."
                    >
                      {describeMatchPoolTier(user.match_pool_tier, user.match_pool_unopened_streak ?? 0)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Verification & Trust (user-360) — surfaces core.verification_trust
                so operators see blue-check / trust score / IDV checks inline,
                without cross-navigating to the Verification queue. */}
            <div className="rounded-lg border bg-card p-6 lg:col-span-2">
              <h3 className="mb-4 text-lg font-semibold">Verification &amp; Trust</h3>

              {/* Reports summary — always shown (safety signal). "Received"
                  links through to the Reports queue filtered to this user. */}
              <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-b pb-4 text-sm">
                <button
                  type="button"
                  onClick={() => navigate(`/reports?reported_user_id=${user.id}`)}
                  className="group inline-flex items-center gap-1.5"
                  title="View reports filed against this user"
                >
                  <span className="text-muted-foreground">Reports received:</span>
                  <strong className={user.reports_received > 0 ? 'text-red-500' : 'text-foreground'}>
                    {user.reports_received}
                  </strong>
                  {user.reports_received > 0 && (
                    <span className="text-xs text-primary underline-offset-2 group-hover:underline">
                      view →
                    </span>
                  )}
                </button>
                <span className="text-muted-foreground">
                  Reports filed: <strong className="text-foreground">{user.reports_filed}</strong>
                </span>
              </div>

              {(() => {
                const hasAny =
                  user.blue_check != null ||
                  user.trust_score != null ||
                  user.verification_overall_status != null ||
                  user.id_verification_status != null ||
                  user.government_id_verified != null ||
                  user.email_verified != null ||
                  user.phone_verified != null;
                if (!hasAny) {
                  return (
                    <p className="text-sm text-muted-foreground">
                      No verification record for this user.
                    </p>
                  );
                }
                const score = user.trust_score;
                const scoreColor =
                  score == null
                    ? 'text-muted-foreground'
                    : score >= 80
                      ? 'text-green-500'
                      : score >= 50
                        ? 'text-amber-500'
                        : 'text-red-500';
                const checks: Array<[string, boolean | null | undefined]> = [
                  ['Government ID', user.government_id_verified],
                  ['Face match', user.face_match_verified],
                  ['Liveness', user.liveness_verified],
                  ['Email', user.email_verified],
                  ['Phone', user.phone_verified],
                ];
                return (
                  <>
                    <div className="mb-4 flex flex-wrap items-center gap-3">
                      {user.blue_check ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 px-3 py-1 text-sm font-semibold text-sky-500">
                          ✓ Blue-check verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground">
                          Not blue-check verified
                        </span>
                      )}
                      {score != null && (
                        <span className="text-sm text-muted-foreground">
                          Trust score:{' '}
                          <strong className={`text-base ${scoreColor}`}>{score}</strong>
                        </span>
                      )}
                      {user.verification_overall_status && (
                        <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs capitalize text-muted-foreground">
                          {user.verification_overall_status.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {checks.map(([label, ok]) => (
                        <div
                          key={label}
                          className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-sm"
                        >
                          <span className="text-muted-foreground">{label}</span>
                          <span
                            className={
                              ok === true
                                ? 'font-semibold text-green-500'
                                : ok === false
                                  ? 'text-muted-foreground'
                                  : 'text-muted-foreground italic'
                            }
                          >
                            {ok === true ? '✓ Yes' : ok === false ? '— No' : '— Not checked'}
                          </span>
                        </div>
                      ))}
                      {user.id_verification_status && (
                        <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-sm">
                          <span className="text-muted-foreground">IDV status</span>
                          <span className="font-medium capitalize">
                            {user.id_verification_status.replace(/_/g, ' ')}
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Unified Trust & Safety timeline (user-360) */}
            <SafetyTimelineCard userId={id!} />

            {/* Internal admin notes (user-360) — self-gated on users.notes.manage */}
            <UserNotesCard userId={id!} />

            {/* Bio */}
            {user.bio && (
              <div className="rounded-lg border bg-card p-6 lg:col-span-2">
                <h3 className="mb-4 text-lg font-semibold">Bio</h3>
                <p className="whitespace-pre-wrap text-muted-foreground">{user.bio}</p>
              </div>
            )}

            {/* Identity & lifestyle — canonical "about me" facts from
                hard_filters.responses_me. Same data the Matching Preferences
                tab reads; surfaced here as the 30-second matchmaker snapshot.
                Grouped by category (demographics, physical, lifestyle, etc.). */}
            {user.identity_hard_filters && user.identity_hard_filters.length > 0 && (
              <div className="rounded-lg border bg-card p-6 lg:col-span-2">
                <h3 className="mb-2 text-lg font-semibold">Identity & lifestyle</h3>
                <p className="mb-4 text-xs text-muted-foreground">
                  hard_filters.responses_me — the canonical "about me" answers (also visible in the Matching Preferences tab).
                </p>

                {(() => {
                  // Group by category for matchmaker readability.
                  const groups = new Map<string, IdentityHardFilter[]>();
                  for (const row of user.identity_hard_filters) {
                    const cat = row.category ?? 'other';
                    if (!groups.has(cat)) groups.set(cat, []);
                    groups.get(cat)!.push(row);
                  }
                  // Preferred category order: demographics → physical → lifestyle → background → relationship → other.
                  const order = ['demographics', 'physical', 'lifestyle', 'background', 'relationship'];
                  const sortedCats = Array.from(groups.keys()).sort(
                    (a, b) => (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b)),
                  );

                  return (
                    <div className="space-y-5">
                      {sortedCats.map((cat) => (
                        <div key={cat}>
                          <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{cat}</div>
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {groups.get(cat)!.map((row) => {
                              const value =
                                row.selected_options && row.selected_options.length > 0
                                  ? row.selected_options.join(', ')
                                  : row.numeric_value != null
                                    ? `${row.numeric_value}${row.numeric_unit ? ' ' + row.numeric_unit : ''}`
                                    : null;
                              const label = row.question_key
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, (c) => c.toUpperCase());
                              return <Field key={row.question_key} label={label} value={value} />;
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Private location & physical — slim (only the two columns of
                core.profiles_private that are actually populated: height_cm
                and precise_location). Identity moved to the card above. */}
            {user.private_profile && (
              user.private_profile.height_cm != null
              || user.private_profile.precise_lat != null
            ) && (
              <div className="rounded-lg border bg-card p-6 lg:col-span-2">
                <h3 className="mb-2 text-lg font-semibold">Private location & physical</h3>
                <p className="mb-4 text-xs text-muted-foreground">
                  core.profiles_private — high-precision data, matchmakers only.
                </p>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {user.private_profile.height_cm != null && (
                    <Field label="Height" value={`${user.private_profile.height_cm} cm`} />
                  )}
                  {user.private_profile.precise_lat != null && user.private_profile.precise_lon != null && (
                    <Field
                      label="Precise location"
                      value={`${user.private_profile.precise_lat.toFixed(5)}, ${user.private_profile.precise_lon.toFixed(5)}`}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'wizard' && (
          <div className="space-y-6">
            <div className="rounded-lg border bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold">Setup Wizard Progress</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-lg bg-muted/50 p-4">
                  <div>
                    <div className="font-medium">Signup wizard</div>
                    <div className="text-sm text-muted-foreground">Phone/email + basic identity captured</div>
                  </div>
                  <span className={`font-medium ${user.signup_wizard_complete ? 'text-green-600' : 'text-yellow-600'}`}>
                    {user.signup_wizard_complete ? 'Complete' : 'Incomplete'}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-muted/50 p-4">
                  <div>
                    <div className="font-medium">Profile complete</div>
                    <div className="text-sm text-muted-foreground">core.profiles_public populated (display name, gender, photos, bio)</div>
                  </div>
                  <span className={`font-medium ${user.profile_complete ? 'text-green-600' : 'text-yellow-600'}`}>
                    {user.profile_complete ? 'Complete' : 'Incomplete'}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-muted/50 p-4">
                  <div>
                    <div className="font-medium">Hard filters</div>
                    <div className="text-sm text-muted-foreground">hard_filters.responses_me + responses_looking_for answered</div>
                  </div>
                  <span className={`font-medium ${user.hard_filters_complete ? 'text-green-600' : 'text-yellow-600'}`}>
                    {user.hard_filters_complete ? 'Complete' : 'Incomplete'}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-muted/50 p-4">
                  <div>
                    <div className="font-medium">Onboarding — match-eligibility gate</div>
                    <div className="text-sm text-muted-foreground">Round-1 (Anchors) complete — the canonical matching gate (source-agnostic: self-answers count the same as Abby-guided). Independent of the Abby-voice onboarding flag below.</div>
                  </div>
                  <div className="text-right">
                    <span className={`font-medium ${eligibilityTone(user.matching_eligibility)}`}>
                      {eligibilityWord(user.matching_eligibility)}
                    </span>
                    {/* This printed the machine reason itself until 2026-10-30
                        ("round1_incomplete_3_of_35", "soft_deleted"), which an
                        operator has to decode. Same clause map as the create
                        screen. */}
                    {user.matching_eligibility && !user.matching_eligibility.eligible && (
                      <div className="text-xs text-muted-foreground">
                        {describeMatchBlock(user.matching_eligibility.reason)}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground/70">
                      Abby voice: {user.onboarding_complete ? 'complete' : 'incomplete'}
                      {user.onboarding_step != null ? ` (step ${user.onboarding_step})` : ''}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border bg-card p-6">
              <h3 className="mb-2 text-lg font-semibold">Wizard-captured fields</h3>
              <p className="mb-4 text-xs text-muted-foreground">
                core.users_core + core.profiles_public — fields the signup wizard
                populates. Identity / matching answers (sexual orientation,
                relationship type/status, looking-for, search radius, etc.)
                were moved to the hard-filters wizard and now appear on the
                Profile tab's "Identity & lifestyle" card.
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Display name" value={user.display_name} />
                <Field label="First name" value={user.first_name} />
                <Field label="Last name" value={user.last_name} />
                <Field label="Email" value={user.email} />
                <Field label="Phone" value={user.phone} />
                <Field label="Birthdate" value={user.birthdate ? formatBirthdate(user.birthdate) : null} />
                <Field label="Gender" value={user.gender} />
                <Field label="Pronouns" value={user.pronouns} />
                <Field label="Locale" value={user.locale} />
                <Field label="Timezone" value={user.timezone} />
              </div>
              {user.interests && user.interests.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm text-muted-foreground mb-2">Interests</div>
                  <div className="flex flex-wrap gap-2">
                    {user.interests.map((i) => (
                      <span key={i} className="rounded-full bg-primary/10 px-3 py-1 text-xs">{i}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'onboarding' && (
          <div className="space-y-6">
            {onboardingLoading ? (
              <div className="flex h-32 items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : onboardingError ? (
              <div className="rounded-lg border border-destructive bg-destructive/10 p-6">
                <div className="font-medium text-destructive">Failed to load onboarding answers</div>
                <button onClick={() => refetchOnboarding()} className="mt-2 text-sm underline">Retry</button>
              </div>
            ) : !onboarding ? (
              <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
                No onboarding data
              </div>
            ) : (
              <>
                <div className="rounded-lg border bg-card p-6">
                  <h3 className="mb-4 text-lg font-semibold">Completion</h3>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
                    <Stat label="Total active questions" value={onboarding.completion.total_active_questions} />
                    <Stat label="Abby-guided questions" value={onboarding.completion.total_abby_guided_questions} />
                    <Stat label="Self-answers" value={onboarding.completion.answered_count} />
                    <Stat label="Abby-guided answered" value={onboarding.completion.abby_guided_answered} />
                    <Stat label="Section summaries" value={onboarding.completion.summaries_count ?? 0} />
                  </div>
                </div>

                {/* Section summaries — Abby's LLM-generated digests, the most
                    matchmaker-readable surface. Top of the tab so it's read
                    first; raw answers stay below as forensic fallback. */}
                <div className="rounded-lg border bg-card p-6">
                  <h3 className="mb-2 text-lg font-semibold">
                    Section summaries ({onboarding.summaries?.length ?? 0})
                  </h3>
                  <p className="mb-4 text-xs text-muted-foreground">
                    onboarding.summaries — Abby's per-section narrative digests, presented to the user at the end of each category.
                  </p>
                  {!onboarding.summaries || onboarding.summaries.length === 0 ? (
                    <p className="text-muted-foreground">No summaries generated yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {onboarding.summaries.map((s) => {
                        const showSpanish = (user.locale ?? '').toLowerCase().startsWith('es') && !!s.summary_es;
                        return (
                          <div key={s.id} className="rounded-lg bg-muted/50 p-4">
                            <div className="flex items-baseline justify-between gap-4">
                              <div className="flex-1">
                                <div className="text-xs text-muted-foreground">
                                  {s.section_slug ?? s.section_id} · round {s.round}
                                  {s.source_answer_count != null && ` · ${s.source_answer_count} answers`}
                                  {s.llm_model && ` · ${s.llm_model}`}
                                  {s.llm_latency_ms != null && ` · ${s.llm_latency_ms}ms`}
                                </div>
                                <div className="font-medium">
                                  {s.section_title ?? s.section_slug ?? s.section_id}
                                </div>
                              </div>
                              <div className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatDate(s.generated_at)}
                              </div>
                            </div>
                            <p className="mt-2 whitespace-pre-wrap text-sm">{s.summary}</p>
                            {showSpanish && (
                              <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground italic">
                                ES: {s.summary_es}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Canonical matching preferences (core.match_preferences).
                    Distinct from the granular preference-answers list below. */}
                <div className="rounded-lg border bg-card p-6">
                  <h3 className="mb-2 text-lg font-semibold">Matching preferences</h3>
                  <p className="mb-4 text-xs text-muted-foreground">
                    core.match_preferences — the user's current effective matching configuration.
                  </p>
                  {!onboarding.match_preferences ? (
                    <p className="text-muted-foreground">No match preferences saved yet.</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <Field
                          label="Age range"
                          value={
                            onboarding.match_preferences.pref_age_min != null || onboarding.match_preferences.pref_age_max != null
                              ? `${onboarding.match_preferences.pref_age_min ?? '?'} – ${onboarding.match_preferences.pref_age_max ?? '?'}`
                              : (onboarding.match_preferences.age_min != null || onboarding.match_preferences.age_max != null
                                  ? `${onboarding.match_preferences.age_min ?? '?'} – ${onboarding.match_preferences.age_max ?? '?'}`
                                  : null)
                          }
                        />
                        <Field
                          label="Distance (km)"
                          value={
                            onboarding.match_preferences.pref_distance_km != null
                              ? String(onboarding.match_preferences.pref_distance_km)
                              : (onboarding.match_preferences.distance_km != null
                                  ? String(onboarding.match_preferences.distance_km)
                                  : null)
                          }
                        />
                        <Field
                          label="Relationship structure"
                          value={onboarding.match_preferences.pref_relationship_structure}
                        />
                        <Field
                          label="Gender identities"
                          value={
                            onboarding.match_preferences.pref_gender_identities?.length
                              ? onboarding.match_preferences.pref_gender_identities.join(', ')
                              : (onboarding.match_preferences.gender_preferences?.length
                                  ? onboarding.match_preferences.gender_preferences.join(', ')
                                  : null)
                          }
                        />
                        <Field
                          label="Ethnicities"
                          value={
                            onboarding.match_preferences.pref_ethnicities?.length
                              ? onboarding.match_preferences.pref_ethnicities.join(', ')
                              : null
                          }
                        />
                        <Field
                          label="Languages"
                          value={
                            onboarding.match_preferences.pref_languages?.length
                              ? onboarding.match_preferences.pref_languages.join(', ')
                              : null
                          }
                        />
                      </div>
                      {onboarding.match_preferences.pref_lifestyles != null && (
                        <div className="mt-4">
                          <div className="text-sm text-muted-foreground mb-2">Lifestyles</div>
                          <pre className="rounded-lg bg-muted/30 p-3 text-xs overflow-x-auto">
                            {JSON.stringify(onboarding.match_preferences.pref_lifestyles, null, 2)}
                          </pre>
                        </div>
                      )}
                      {onboarding.match_preferences.updated_at && (
                        <div className="mt-3 text-xs text-muted-foreground">
                          Last updated: {formatDate(onboarding.match_preferences.updated_at)}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="rounded-lg border bg-card p-6">
                  <h3 className="mb-4 text-lg font-semibold">
                    Onboarding answers ({onboarding.answers.length})
                  </h3>
                  {onboarding.answers.length === 0 ? (
                    <p className="text-muted-foreground">No answers recorded yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {onboarding.answers.map((a) => (
                        <div key={`${a.question_id}-${a.answered_at}`} className="rounded-lg bg-muted/50 p-4">
                          <div className="flex items-baseline justify-between gap-4">
                            <div className="flex-1">
                              <div className="text-xs text-muted-foreground">
                                {a.question_code} · {a.screen_group ?? 'unknown'} · #{a.display_order}
                              </div>
                              <div className="font-medium">{a.question_text}</div>
                            </div>
                            <div className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDate(a.answered_at)}
                            </div>
                          </div>
                          <div className="mt-2">
                            {a.option_texts.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {a.option_texts.map((opt, i) => (
                                  <span key={i} className="rounded-full bg-primary/15 px-3 py-1 text-xs">{opt}</span>
                                ))}
                              </div>
                            ) : a.text_value ? (
                              <p className="whitespace-pre-wrap text-sm">{a.text_value}</p>
                            ) : a.numeric_value != null ? (
                              <p className="text-sm">{a.numeric_value}</p>
                            ) : a.raw_response ? (
                              <p className="whitespace-pre-wrap text-sm text-muted-foreground italic">
                                raw: {a.raw_response}
                              </p>
                            ) : (
                              <p className="text-xs text-muted-foreground italic">(empty answer)</p>
                            )}
                          </div>
                          {a.parsed_by && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              parsed by: {a.parsed_by}
                              {a.parse_confidence != null && ` · confidence: ${(a.parse_confidence * 100).toFixed(0)}%`}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {onboarding.preference_answers.length > 0 && (
                  <div className="rounded-lg border bg-card p-6">
                    <h3 className="mb-4 text-lg font-semibold">
                      Preference answers ({onboarding.preference_answers.length})
                    </h3>
                    <div className="space-y-3">
                      {onboarding.preference_answers.map((p) => (
                        <div key={`${p.question_id}-${p.answered_at}`} className="rounded-lg bg-muted/50 p-4">
                          <div className="flex items-baseline justify-between gap-4">
                            <div className="flex-1">
                              <div className="text-xs text-muted-foreground">
                                {p.question_code} · {p.screen_group ?? 'unknown'} · #{p.display_order}
                                {p.is_dealbreaker && (
                                  <span className="ml-2 rounded bg-destructive/15 px-2 py-0.5 text-destructive">DEALBREAKER</span>
                                )}
                                {p.importance && <span className="ml-2">importance: {p.importance}</span>}
                              </div>
                              <div className="font-medium">{p.question_text}</div>
                            </div>
                            <div className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDate(p.answered_at)}
                            </div>
                          </div>
                          <div className="mt-2">
                            {p.acceptable_option_texts.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {p.acceptable_option_texts.map((opt, i) => (
                                  <span key={i} className="rounded-full bg-secondary/30 px-3 py-1 text-xs">{opt}</span>
                                ))}
                              </div>
                            ) : (p.min_value != null || p.max_value != null) ? (
                              <p className="text-sm">
                                Range: {p.min_value ?? '−∞'} to {p.max_value ?? '+∞'}
                              </p>
                            ) : (
                              <p className="text-xs text-muted-foreground italic">(no constraint set)</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'coaching' && (
          <div className="space-y-6">
            {abbySessionsLoading ? (
              <div className="flex h-32 items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : abbySessionsError ? (
              <div className="rounded-lg border border-destructive bg-destructive/10 p-6">
                <div className="font-medium text-destructive">Failed to load Abby coaching sessions</div>
                <button onClick={() => refetchAbbySessions()} className="mt-2 text-sm underline">Retry</button>
              </div>
            ) : !abbySessionsData?.data ? (
              <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
                No coaching data
              </div>
            ) : (
              <>
                {/* Activity counters from core.coaching_events. Lets the
                    matchmaker distinguish "user never coached" from "user
                    coached extensively but no summary persisted" — see the
                    forensic dive at 2026-05-26 for why this matters. */}
                <div className="rounded-lg border bg-card p-6">
                  <h3 className="mb-2 text-lg font-semibold">Voice activity</h3>
                  <p className="mb-4 text-xs text-muted-foreground">
                    core.coaching_events — every tool call Abby has made for this user.
                  </p>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <Stat label="Tool calls" value={abbySessionsData.data.activity.total_tool_calls} />
                    <Stat label="Distinct sessions" value={abbySessionsData.data.activity.distinct_session_ids} />
                    <Stat label="Summaries persisted" value={abbySessionsData.data.sessions.length} />
                    <Stat label="Session-end events" value={abbySessionsData.data.activity.session_completed_events} />
                  </div>
                  {abbySessionsData.data.activity.last_tool_call_at && (
                    <div className="mt-3 text-xs text-muted-foreground">
                      Last tool call: {formatDate(abbySessionsData.data.activity.last_tool_call_at)}
                    </div>
                  )}
                </div>

                <div className="rounded-lg border bg-card p-6">
                  <h3 className="mb-2 text-lg font-semibold">
                    Abby coaching sessions ({abbySessionsData.data.sessions.length})
                  </h3>
                  <p className="mb-4 text-xs text-muted-foreground">
                    Post-session summaries written by Abby's <code>create_session_summary</code> /
                    {' '}<code>end_coaching_session</code> tools. Stored at
                    {' '}<code>s3://&lt;profile-bucket&gt;/users/{`{userId}`}/session-summaries/{`{sessionId}`}.json</code>.
                  </p>

                  {abbySessionsData.data.sessions.length === 0 ? (
                    <div className="rounded-lg border border-dashed p-6 text-center">
                      <Sparkles className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
                      {abbySessionsData.data.activity.total_tool_calls === 0 ? (
                        <>
                          <p className="text-muted-foreground">
                            This user hasn't engaged Abby yet.
                          </p>
                          <p className="mt-2 text-xs text-muted-foreground">
                            Zero tool calls recorded in core.coaching_events. The Abby Coaching tab
                            will populate after their first voice session.
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-muted-foreground">
                            This user has engaged Abby{' '}
                            ({abbySessionsData.data.activity.total_tool_calls} tool calls across{' '}
                            {abbySessionsData.data.activity.distinct_session_ids} sessions) but no
                            session has been formally summarized.
                          </p>
                          <p className="mt-2 text-xs text-muted-foreground">
                            S3 summaries only persist when Abby invokes <code>end_coaching_session</code>{' '}
                            with summary fields at the close of a COACHING-phase session. The
                            coaching behavioral eval shows the LLM does this in ~5–10% of sessions
                            — a known reliability gap, tracked separately.
                          </p>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {abbySessionsData.data.sessions.map((s) => {
                        const isExpanded = expandedSessionId === s.id;
                        return (
                          <div key={s.id} className="rounded-lg bg-muted/50">
                            <button
                              type="button"
                              onClick={() => setExpandedSessionId(isExpanded ? null : s.id)}
                              className="flex w-full items-center gap-3 p-4 text-left hover:bg-muted/70"
                            >
                              <div className="text-muted-foreground">
                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-baseline justify-between gap-4">
                                  <div className="font-medium">
                                    {formatDate(s.timestamp)}
                                    {s.duration_minutes != null && (
                                      <span className="ml-2 text-xs text-muted-foreground">
                                        ({s.duration_minutes} min)
                                      </span>
                                    )}
                                  </div>
                                  {s.emotional_tone && (
                                    <span className="rounded-full bg-primary/15 px-3 py-1 text-xs capitalize">
                                      {s.emotional_tone}
                                    </span>
                                  )}
                                </div>
                                {s.topics_covered.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {s.topics_covered.map((t) => (
                                      <span key={t} className="rounded-full bg-secondary/30 px-3 py-1 text-xs">{t}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </button>

                            {isExpanded && (
                              <div className="border-t bg-card p-4 space-y-4">
                                <div>
                                  <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                                    Conversation summary
                                  </div>
                                  <p className="whitespace-pre-wrap text-sm">{s.conversation_summary}</p>
                                </div>

                                {s.key_insights.length > 0 && (
                                  <div>
                                    <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                                      Key insights
                                    </div>
                                    <ul className="list-disc space-y-1 pl-5 text-sm">
                                      {s.key_insights.map((i, idx) => <li key={idx}>{i}</li>)}
                                    </ul>
                                  </div>
                                )}

                                {s.action_items.length > 0 && (
                                  <div>
                                    <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                                      Action items
                                    </div>
                                    <ul className="list-disc space-y-1 pl-5 text-sm">
                                      {s.action_items.map((a, idx) => <li key={idx}>{a}</li>)}
                                    </ul>
                                  </div>
                                )}

                                {s.related_match_ids && s.related_match_ids.length > 0 && (
                                  <div>
                                    <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                                      Related matches
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {s.related_match_ids.map((mid) => (
                                        <code key={mid} className="rounded bg-muted px-2 py-1 text-xs">{mid}</code>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                <div className="text-xs text-muted-foreground">
                                  session_id: <code>{s.session_id}</code>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'photos' && (
          <div>
            {photosLoading ? (
              <div className="flex h-32 items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : photos.length === 0 ? (
              <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
                No photos uploaded
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {photos.map((photo) => (
                  <div key={photo.id} className="relative aspect-square overflow-hidden rounded-lg border">
                    <img
                      src={photo.url}
                      alt="User photo"
                      className="h-full w-full object-cover"
                    />
                    {photo.is_primary && (
                      <div className="absolute left-2 top-2 rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
                        Primary
                      </div>
                    )}
                    {photo.visibility && (
                      <div
                        className={`absolute right-2 top-2 rounded-full px-2 py-0.5 text-xs font-medium text-white ${
                          photo.visibility === 'private' ? 'bg-violet-600' : 'bg-blue-600'
                        }`}
                      >
                        {photo.visibility === 'private' ? 'Private' : 'Public'}
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                      <div className="text-xs text-white">
                        {formatDate(photo.uploaded_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'matches' && (
          <div>
            {matchesLoading ? (
              <div className="flex h-32 items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : matches.length === 0 ? (
              <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
                No matches yet
              </div>
            ) : (
              <div className="rounded-lg border bg-card overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Matched With</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground" title="My decision / Their decision">Decisions</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground" title="Photos shared (mine / theirs)">Pics</th>
                      {/* `Msgs` column header removed 2026-05-11 — see UserDetails
                          interface comment for context. */}
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Matched On</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matches.map((match) => (
                      <tr key={match.id} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3">
                          <button
                            onClick={() => navigate(`/users/${match.matched_user_id}`)}
                            className="font-medium text-primary hover:underline text-left block"
                          >
                            {match.matched_user_name || '(no name)'}
                          </button>
                          {match.matched_user_email && (
                            <div className="text-xs text-muted-foreground truncate max-w-xs">
                              {match.matched_user_email}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3"><MatchStatusBadge status={match.status} unmatched_at={match.unmatched_at} /></td>
                        <td className="px-4 py-3 text-center text-sm whitespace-nowrap">
                          <DecisionGlyph value={match.my_decision} />
                          <span className="mx-1 text-muted-foreground">/</span>
                          <DecisionGlyph value={match.their_decision} />
                        </td>
                        <td className="px-4 py-3 text-center text-sm whitespace-nowrap font-mono text-muted-foreground" title="✓ = shared their photos with the match">
                          <span className={match.my_pics_shared ? 'text-foreground' : ''}>{match.my_pics_shared ? '✓' : '·'}</span>
                          <span className="mx-1">/</span>
                          <span className={match.their_pics_shared ? 'text-foreground' : ''}>{match.their_pics_shared ? '✓' : '·'}</span>
                        </td>
                        {/* `messages_count` cell removed 2026-05-11 — header above
                            was removed in the same commit. */}
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                          {formatDate(match.matched_at)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => navigate(`/users/${match.matched_user_id}`)}
                            className="text-sm text-primary hover:underline"
                          >
                            View User
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <div>
            {activityError ? (
              <div className="rounded-lg border border-dashed p-8 text-center">
                <AlertTriangle className="mx-auto h-12 w-12 mb-3 text-destructive opacity-70" />
                <p className="font-medium text-destructive">Failed to load activity</p>
                <p className="text-sm text-muted-foreground mt-1">
                  There was an error loading the user's activity log.
                </p>
                <button
                  onClick={() => refetchActivity()}
                  className="mt-4 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : activityLoading ? (
              <div className="flex h-32 items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : activities.length === 0 ? (
              <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
                <Activity className="mx-auto h-12 w-12 mb-3 opacity-50" />
                <p className="font-medium">No activity recorded</p>
                <p className="text-sm mt-1">User activity will appear here as they interact with the platform.</p>
              </div>
            ) : (
              <div className="rounded-lg border bg-card">
                <div className="px-4 py-3 border-b bg-muted/50">
                  <h3 className="text-sm font-medium">Recent Activity</h3>
                </div>
                <div className="divide-y">
                  {activities.map((activity) => {
                    const IconComponent = getActivityIcon(activity.action);
                    return (
                      <div key={activity.id} className="flex items-start gap-4 px-4 py-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                          <IconComponent className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm capitalize">
                              {activity.action.replace(/_/g, ' ')}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(activity.created_at)}
                            </span>
                          </div>
                          {activity.details && (
                            <p className="text-sm text-muted-foreground mt-0.5 truncate">
                              {activity.details}
                            </p>
                          )}
                          {activity.ip_address && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              IP: {activity.ip_address}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'hardFilters' && (
          <div>
            {hardFiltersError ? (
              <div className="rounded-lg border border-dashed p-8 text-center">
                <AlertTriangle className="mx-auto h-12 w-12 mb-3 text-destructive opacity-70" />
                <p className="font-medium text-destructive">Failed to load hard filters</p>
                <p className="text-sm text-muted-foreground mt-1">
                  There was an error loading the user's hard filter data.
                </p>
                <button
                  onClick={() => refetchHardFilters()}
                  className="mt-4 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : hardFiltersLoading ? (
              <div className="space-y-6 animate-pulse">
                {/* Skeleton: Completion Summary card with 3 stat placeholders */}
                <div className="rounded-lg border bg-card p-6">
                  <div className="h-5 w-48 rounded bg-muted mb-4"></div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="h-7 w-16 rounded bg-muted mb-2"></div>
                      <div className="h-4 w-24 rounded bg-muted"></div>
                    </div>
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="h-7 w-16 rounded bg-muted mb-2"></div>
                      <div className="h-4 w-24 rounded bg-muted"></div>
                    </div>
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="h-7 w-16 rounded bg-muted mb-2"></div>
                      <div className="h-4 w-24 rounded bg-muted"></div>
                    </div>
                  </div>
                </div>
                {/* Skeleton: Table with 3 row placeholders */}
                <div className="rounded-lg border bg-card">
                  <div className="px-4 py-3 border-b bg-muted/50">
                    <div className="h-4 w-40 rounded bg-muted"></div>
                  </div>
                  <div className="divide-y">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-4 px-4 py-3">
                        <div className="h-4 w-24 rounded bg-muted"></div>
                        <div className="h-4 w-20 rounded bg-muted"></div>
                        <div className="h-4 w-16 rounded bg-muted"></div>
                        <div className="h-4 w-32 rounded bg-muted"></div>
                        <div className="h-4 w-16 rounded bg-muted"></div>
                        <div className="h-4 w-24 rounded bg-muted"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : !hardFilters || (hardFilters.self_answers.length === 0 && hardFilters.preference_answers.length === 0) ? (
              <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
                <Filter className="mx-auto h-12 w-12 mb-3 opacity-50" />
                <p className="font-medium">No hard filter data</p>
                <p className="text-sm mt-1">Hard filter answers will appear here once the user completes them.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Completion Summary */}
                <div className="rounded-lg border bg-card p-6">
                  <h3 className="mb-4 text-lg font-semibold">Completion Summary</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="text-2xl font-bold">{hardFilters.completion.self_answered}/{hardFilters.completion.total_questions}</div>
                      <div className="text-sm text-muted-foreground">Self Answered</div>
                    </div>
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="text-2xl font-bold">{hardFilters.completion.preference_answered}/{hardFilters.completion.total_questions}</div>
                      <div className="text-sm text-muted-foreground">Preferences Set</div>
                    </div>
                    <div className="rounded-lg bg-muted/50 p-4">
                      <div className="text-2xl font-bold">{hardFilters.completion.dealbreaker_count}</div>
                      <div className="text-sm text-muted-foreground">Dealbreakers</div>
                    </div>
                  </div>
                </div>

                {/* Self-Description Answers */}
                {hardFilters.self_answers.length > 0 && (
                  <div className="rounded-lg border bg-card">
                    <div className="px-4 py-3 border-b bg-muted/50">
                      <h3 className="text-sm font-medium">Self-Description Answers</h3>
                    </div>
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Question</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Category</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Type</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Selected Options</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Numeric Value</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Last Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hardFilters.self_answers.map((answer) => (
                          <tr key={answer.question_key} className="border-b last:border-0">
                            <td className="px-4 py-3 text-sm font-medium">{getQuestionLabel(answer.question_key)}</td>
                            <td className="px-4 py-3 text-sm text-muted-foreground capitalize">{answer.category}</td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">{answer.answer_type}</td>
                            <td className="px-4 py-3 text-sm">
                              {answer.selected_options && answer.selected_options.length > 0
                                ? answer.selected_options.join(', ')
                                : <span className="text-muted-foreground">-</span>}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {answer.numeric_value !== null
                                ? answer.numeric_value
                                : <span className="text-muted-foreground">-</span>}
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(answer.updated_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Preference Answers */}
                {hardFilters.preference_answers.length > 0 && (
                  <div className="rounded-lg border bg-card">
                    <div className="px-4 py-3 border-b bg-muted/50">
                      <h3 className="text-sm font-medium">Preference Answers</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Question</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Category</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Type</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Selected Options</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Range</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Dealbreaker</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Importance</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Last Updated</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hardFilters.preference_answers.map((answer) => (
                            <tr key={answer.question_key} className="border-b last:border-0">
                              <td className="px-4 py-3 text-sm font-medium">{getQuestionLabel(answer.question_key)}</td>
                              <td className="px-4 py-3 text-sm text-muted-foreground capitalize">{answer.category}</td>
                              <td className="px-4 py-3 text-sm text-muted-foreground">{answer.answer_type}</td>
                              <td className="px-4 py-3 text-sm">
                                {answer.selected_options && answer.selected_options.length > 0
                                  ? answer.selected_options.join(', ')
                                  : <span className="text-muted-foreground">-</span>}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {answer.range_min !== null && answer.range_max !== null
                                  ? `${answer.range_min} - ${answer.range_max}`
                                  : <span className="text-muted-foreground">-</span>}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {answer.is_dealbreaker && (
                                  <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                                    Dealbreaker
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-muted-foreground capitalize">{answer.importance || '-'}</td>
                              <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(answer.updated_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Coupons & Promotions — always visible regardless of active tab */}
        {activeTab === 'profile' && (
          <section className="rounded-lg border bg-card p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Gift className="w-5 h-5" />
                Coupons &amp; Promotions
              </h2>
              <button
                onClick={() => setGrantModalOpen(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90"
              >
                <Gift className="h-4 w-4" />
                Grant Promotion
              </button>
            </div>
            {couponsLoading ? (
              <p className="text-sm text-muted-foreground">Loading coupon data…</p>
            ) : (
              <>
                {/* Active claims */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium mb-2">
                    Active Claims ({couponsData?.data?.total_claims ?? 0})
                  </h3>
                  {!couponsData?.data?.claims?.length ? (
                    <p className="text-sm text-muted-foreground">No coupon claims for this user.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="border-b text-left text-muted-foreground">
                            <th className="py-2 pr-4 font-medium">Promotion</th>
                            <th className="py-2 pr-4 font-medium">Source</th>
                            <th className="py-2 pr-4 font-medium">Paywall</th>
                            <th className="py-2 pr-4 font-medium">Remaining / Granted</th>
                            <th className="py-2 pr-4 font-medium">Status</th>
                            <th className="py-2 pr-4 font-medium">Expires</th>
                            <th className="py-2 font-medium">Granted At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {couponsData.data.claims.map((claim) => (
                            <tr key={claim.claim_id} className="border-b last:border-0 hover:bg-muted/30">
                              <td className="py-2 pr-4 font-medium">{claim.promotion_name}</td>
                              <td className="py-2 pr-4 text-muted-foreground">{claim.source}</td>
                              <td className="py-2 pr-4">{claim.paywall_type}</td>
                              <td className="py-2 pr-4">
                                <span className={claim.coupons_remaining > 0 ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
                                  {claim.coupons_remaining}
                                </span>
                                <span className="text-muted-foreground"> / {claim.coupons_granted}</span>
                              </td>
                              <td className="py-2 pr-4">
                                <span className={cn(
                                  'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
                                  claim.status === 'active'
                                    ? 'bg-green-100 text-green-700 border-green-200'
                                    : claim.status === 'revoked'
                                    ? 'bg-red-100 text-red-700 border-red-200'
                                    : 'bg-gray-100 text-gray-600 border-gray-200',
                                )}>
                                  {claim.status}
                                </span>
                              </td>
                              <td className="py-2 pr-4 text-muted-foreground">
                                {claim.expires_at ? formatDate(claim.expires_at) : '—'}
                              </td>
                              <td className="py-2 text-muted-foreground">
                                {formatDate(claim.granted_at)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Recent redemptions */}
                <div>
                  <h3 className="text-sm font-medium mb-2">
                    Recent Redemptions ({couponsData?.data?.total_redemptions ?? 0})
                  </h3>
                  {!couponsData?.data?.redemptions?.length ? (
                    <p className="text-sm text-muted-foreground">No redemptions for this user.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="border-b text-left text-muted-foreground">
                            <th className="py-2 pr-4 font-medium">Promotion</th>
                            <th className="py-2 pr-4 font-medium">Paywall</th>
                            <th className="py-2 pr-4 font-medium">Event ID</th>
                            <th className="py-2 pr-4 font-medium">Status</th>
                            <th className="py-2 pr-4 font-medium">Held At</th>
                            <th className="py-2 font-medium">Consumed / Refunded</th>
                          </tr>
                        </thead>
                        <tbody>
                          {couponsData.data.redemptions.slice(0, 10).map((r) => (
                            <tr key={r.redemption_id} className="border-b last:border-0 hover:bg-muted/30">
                              <td className="py-2 pr-4 font-medium">{r.promotion_name}</td>
                              <td className="py-2 pr-4">{r.paywall_type}</td>
                              <td className="py-2 pr-4 font-mono text-xs text-muted-foreground truncate max-w-[120px]">
                                {r.paywall_event_id}
                              </td>
                              <td className="py-2 pr-4">
                                <span className={cn(
                                  'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
                                  r.status === 'consumed'
                                    ? 'bg-green-100 text-green-700 border-green-200'
                                    : r.status === 'held'
                                    ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                                    : 'bg-gray-100 text-gray-600 border-gray-200',
                                )}>
                                  {r.status}
                                </span>
                              </td>
                              <td className="py-2 pr-4 text-muted-foreground">{formatDate(r.held_at)}</td>
                              <td className="py-2 text-muted-foreground">
                                {r.consumed_at
                                  ? formatDate(r.consumed_at)
                                  : r.refunded_at
                                  ? `Refunded ${formatDate(r.refunded_at)}`
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </section>
        )}

        {/* Subscription — always visible on profile tab */}
        {activeTab === 'profile' && (
          <section className="rounded-lg border bg-card p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Subscription
              </h2>
              {hasPermission('users.coupon.grant') && (
                <button
                  onClick={() => setTierModalOpen(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90"
                >
                  <CreditCard className="h-4 w-4" />
                  Set Tier
                </button>
              )}
            </div>
            <div className="text-sm text-muted-foreground mb-1">Current tier</div>
            <div className="text-2xl font-bold capitalize">{user.subscription_tier}</div>
          </section>
        )}
      </div>

      {/* Grant Promotion Modal */}
      <GrantPromotionModal
        open={grantModalOpen}
        onClose={() => setGrantModalOpen(false)}
        presetUserIds={[id ?? '']}
        lockUserIds
        onGranted={() => {
          setGrantModalOpen(false);
          void refetchCoupons();
        }}
      />

      {/* Set Subscription Tier Modal */}
      <SetTierModal
        open={tierModalOpen}
        onClose={() => setTierModalOpen(false)}
        userId={id ?? ''}
        currentTier={user.subscription_tier}
        onTierSet={() => {
          setTierModalOpen(false);
          void refetch();
        }}
      />

      {/* Suspend Modal */}
      {showSuspendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
                <UserX className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Suspend User</h3>
                <p className="text-sm text-muted-foreground">
                  {resolveUserDisplayName(user)}
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              This will prevent the user from logging in and using the platform.
            </p>
            {actionError && (
              <div className="mt-3 rounded-lg border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                {actionError}
              </div>
            )}
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                await handleSuspend(
                  formData.get('reason') as string,
                  formData.get('expires_at') as string || undefined
                );
              }}
              className="mt-4 space-y-4"
            >
              <div>
                <label className="text-sm font-medium">Reason *</label>
                <textarea
                  name="reason"
                  required
                  rows={3}
                  disabled={actionLoading}
                  className="mt-1 w-full rounded-lg border bg-background p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                  placeholder="Enter suspension reason..."
                />
              </div>
              <div>
                <label htmlFor="expires-at" className="text-sm font-medium">Expires (optional)</label>
                <input
                  id="expires-at"
                  type="datetime-local"
                  name="expires_at"
                  disabled={actionLoading}
                  className="mt-1 w-full rounded-lg border bg-background p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowSuspendModal(false);
                    setActionError(null);
                  }}
                  disabled={actionLoading}
                  className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex items-center gap-2 rounded-lg bg-yellow-500 px-4 py-2 text-sm font-medium text-white hover:bg-yellow-600 disabled:opacity-50"
                >
                  {actionLoading && (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  )}
                  Suspend User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tester Toggle Modal (task 7.11). Card is height-constrained
          (`max-h-[90vh]` + `flex-col`, scrollable body, fixed header/footer)
          per tests/unit/lint/admin-modals-constrain-height.test.ts — a new
          modal must not add to the unconstrained count. */}
      {showTesterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-md flex-col rounded-lg bg-card">
            <div className="flex items-center gap-3 border-b p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
                <FlaskConical className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  {user.is_tester ? 'Remove Tester' : 'Mark as Tester'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {resolveUserDisplayName(user)}
                </p>
              </div>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                await handleTesterToggle(testerReason);
              }}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>{TESTER_TOGGLE_LEAD}</p>
                  <ul className="list-disc space-y-1 pl-5">
                    {TESTER_TOGGLE_EFFECTS.map((effect) => (
                      <li key={effect}>{effect}</li>
                    ))}
                  </ul>
                  <p>{TESTER_TOGGLE_AUDIT_NOTE}</p>
                </div>
                {actionError && (
                  <div className="rounded-lg border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                    {actionError}
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium">Reason *</label>
                  <textarea
                    value={testerReason}
                    onChange={(e) => setTesterReason(e.target.value)}
                    required
                    rows={3}
                    maxLength={TESTER_REASON_MAX_LENGTH}
                    disabled={actionLoading}
                    className="mt-1 w-full rounded-lg border bg-background p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                    placeholder="Enter a reason (written to the audit log)..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 border-t p-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowTesterModal(false);
                    setTesterReason('');
                    setActionError(null);
                  }}
                  disabled={actionLoading}
                  className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading || validateTesterReason(testerReason) !== null}
                  className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
                >
                  {actionLoading && (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  )}
                  {user.is_tester ? 'Remove Tester' : 'Mark as Tester'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-card p-6">
            {/* Header with warning icon */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-destructive">Delete User</h3>
                <p className="text-sm text-muted-foreground">Permanent action</p>
              </div>
            </div>

            {/* User info being deleted */}
            <div className="rounded-lg border bg-muted/50 p-4 mb-4">
              <p className="text-sm text-muted-foreground mb-1">You are about to delete:</p>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <User className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium">{resolveUserDisplayName(user)}</p>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
              </div>
            </div>

            {/* Warning message */}
            <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-3 mb-4">
              <p className="text-sm text-destructive font-medium">
                This action is permanent and cannot be undone.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                All user data, photos, matches, and messages will be permanently removed.
              </p>
            </div>

            {actionError && (
              <div className="rounded-lg border border-destructive bg-destructive/10 p-3 text-sm text-destructive mb-4">
                {actionError}
              </div>
            )}

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                await handleDelete(formData.get('reason') as string);
              }}
              className="space-y-4"
            >
              <div>
                <label className="text-sm font-medium">Reason for deletion *</label>
                <textarea
                  name="reason"
                  required
                  rows={3}
                  disabled={actionLoading}
                  className="mt-1 w-full rounded-lg border bg-background p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                  placeholder="Enter deletion reason (required for audit trail)..."
                />
              </div>

              {/* Confirmation checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteConfirmed}
                  onChange={(e) => setDeleteConfirmed(e.target.checked)}
                  disabled={actionLoading}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-destructive focus:ring-destructive"
                />
                <span className="text-sm text-muted-foreground">
                  I understand that this action is <strong className="text-foreground">permanent</strong> and cannot be undone.
                  All data associated with this user will be deleted.
                </span>
              </label>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmed(false);
                    setActionError(null);
                  }}
                  disabled={actionLoading}
                  className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!deleteConfirmed || actionLoading}
                  className="flex items-center gap-2 rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading && (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  )}
                  <Trash2 className="h-4 w-4" />
                  Delete Permanently
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Lightweight presentational helpers used by the Setup Wizard +
// Onboarding Answers tabs. Kept inline to avoid spreading new files.

/**
 * The match-eligibility verdict as one word.
 *
 * 🔴 An ABSENT verdict reads "Not known", never "Not eligible" (2026-10-30).
 * Both call sites used `e?.eligible ? 'Eligible' : 'Not eligible'`, which turns
 * a field the API did not send into a definite answer about a member. It is the
 * same defect the create screen had with the match-pool tier, in the other
 * direction: there, silence read as a pass.
 */
function eligibilityWord(e: { eligible: boolean } | undefined | null): string {
  if (!e) return 'Not known';
  return e.eligible ? 'Eligible' : 'Not eligible';
}

/** The colour that goes with {@link eligibilityWord}; unknown is not a warning. */
function eligibilityTone(e: { eligible: boolean } | undefined | null): string {
  if (!e) return 'text-muted-foreground';
  return e.eligible ? 'text-green-600' : 'text-yellow-600';
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="rounded-lg bg-muted/30 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 font-medium break-words">
        {value ?? <span className="text-muted-foreground italic">—</span>}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-muted/50 p-4">
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

// Color-coded badge for the match.status field. Mirrors the matching-engine
// state machine: presented → mutual → activated → (unmatched/expired/passed).
function MatchStatusBadge({ status, unmatched_at }: { status: string | null; unmatched_at: string | null }) {
  if (unmatched_at) {
    return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">unmatched</span>;
  }
  const s = (status || 'unknown').toLowerCase();
  const tone =
    s === 'mutual' || s === 'activated' || s === 'connected'
      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
      : s === 'presented'
      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
      : s === 'passed' || s === 'rejected'
      ? 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
      : s === 'expired'
      ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
      : s === 'passive'
      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
      : 'bg-muted text-muted-foreground';
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>{s}</span>;
}

function DecisionGlyph({ value }: { value: 'interested' | 'pass' | null | undefined }) {
  if (value === 'interested') return <span className="text-green-600 dark:text-green-400" title="interested">✓</span>;
  if (value === 'pass') return <span className="text-red-600 dark:text-red-400" title="pass">✗</span>;
  return <span className="text-muted-foreground" title="no decision yet">—</span>;
}

// BETA cohort badge cluster shown in the user-show header. Reads the same
// segments the /segments page lists; clicking a badge navigates there for
// revoke (we don't duplicate the revoke UX here — single source of truth
// for grant/revoke is the cohort page).
const SEGMENT_LABEL: Record<string, { label: string; tone: string }> = {
  friends_family: {
    label: 'Friends & Family',
    tone: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  },
  founding_100: {
    label: 'Founding 100',
    tone: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  },
  influencer: {
    label: 'Influencer',
    tone: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  },
  beta_general: {
    label: 'General BETA',
    tone: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  },
};

function SegmentBadges({
  segments,
}: {
  segments: Array<{ segment: string; granted_at: string }>;
}) {
  if (!segments || segments.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {segments.map((s) => {
        const meta = SEGMENT_LABEL[s.segment] ?? {
          label: s.segment,
          tone: 'bg-muted text-muted-foreground',
        };
        return (
          <a
            key={s.segment}
            href="/segments"
            title={`Granted ${formatDate(s.granted_at)} — click to manage on cohort page`}
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium hover:opacity-80 ${meta.tone}`}
          >
            {meta.label}
          </a>
        );
      })}
    </div>
  );
}
